import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function BadgeCelebration({ badge, isPremium = false, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [portalEl, setPortalEl] = useState(null);

  const onCloseRef = useRef(onClose);
  const hideTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const isClosingRef = useRef(false);

  useEffect(() => setPortalEl(document.body), []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const clearTimers = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    hideTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  const requestClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    clearTimers();
    setIsVisible(false);

    closeTimerRef.current = setTimeout(() => {
      onCloseRef.current?.();
      isClosingRef.current = false;
    }, 300);
  }, [clearTimers]);

  useEffect(() => {
    if (!badge) return;

    isClosingRef.current = false;
    clearTimers();
    setIsVisible(true);

    hideTimerRef.current = setTimeout(() => {
      requestClose();
    }, 10000);

    return () => clearTimers();
  }, [badge, clearTimers, requestClose]);

  useEffect(() => {
    if (!badge) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") requestClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [badge, requestClose]);

  if (!badge || !portalEl) return null;

  return createPortal(
    <div
      className={`celebration-overlay ${isVisible ? "visible" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-celebration-title"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className={`badge-card ${isVisible ? "visible" : ""}`}
        data-theme-mode={document?.documentElement?.getAttribute("data-theme") === "dark" ? "dark" : "light"}
      >
        <div className="badge-confetti" aria-hidden="true">
          {Array.from({ length: 22 }).map((_, i) => (
            <span
              key={i}
              className="badge-confetti-piece"
              style={{ left: `${(i * 4.5) % 100}%`, animationDelay: `${(i % 8) * 0.14}s`, background: ["#76a5a7", "#cbe2db", "#9cc9c4", "#f2c98a"][i % 4] }}
            />
          ))}
        </div>

        <div className="badge-medal" aria-hidden="true">
          <span className="badge-medal-emoji">{badge.emoji}</span>
        </div>

        <p className="badge-eyebrow">Un palier atteint</p>

        <h2 id="badge-celebration-title" className="badge-name">{badge.name}</h2>

        <div className="badge-streak">
          <span className="streak-number">{badge.days}</span>
          <span className="streak-label">jours consécutifs</span>
        </div>

        {badge.reward && <p className="badge-unlock-message">{badge.reward}</p>}
        {badge.reward && !isPremium && <p className="badge-unlock-extra">Un nouveau fond d’écran vous attend également.</p>}

        <button
          type="button"
          className="badge-close"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            requestClose();
          }}
        >
          Continuer
        </button>
      </div>
<style>{`
  .celebration-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;

    display: flex;
    align-items: center;
    justify-content: center;

    height: 100svh;

    padding:
      max(16px, env(safe-area-inset-top, 0px) + 16px)
      max(16px, env(safe-area-inset-right, 0px) + 16px)
      max(16px, env(safe-area-inset-bottom, 0px) + 16px)
      max(16px, env(safe-area-inset-left, 0px) + 16px);

    background: rgba(251, 250, 247, 0.34);
    backdrop-filter: blur(6px) saturate(108%);
    -webkit-backdrop-filter: blur(6px) saturate(108%);

    opacity: 0;
    transition: opacity 0.3s ease;

    overflow: hidden;
    touch-action: none;
  }

  .celebration-overlay.visible {
    opacity: 1;
  }

  .badge-card {
    position: relative;
    overflow: hidden;
    width: min(420px, 100%);
    padding: 40px 32px 32px;

    display: flex;
    flex-direction: column;
    align-items: center;

    border: 1px solid rgba(97, 131, 122, 0.16);
    border-radius: 26px;

    background: #ffffff;
    color: #303744;

    box-shadow: 0 24px 60px rgba(49, 72, 66, 0.14);

    text-align: center;

    transform: scale(0.96) translateY(14px);
    opacity: 0;
    transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease;
  }

  .badge-card.visible {
    transform: scale(1) translateY(0);
    opacity: 1;
  }

  .badge-confetti {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: 26px;
    pointer-events: none;
  }

  .badge-confetti-piece {
    position: absolute;
    top: -14px;
    width: 7px;
    height: 11px;
    border-radius: 2px;
    opacity: 0;
    animation: badge-confetti-fall 2.6s cubic-bezier(0.32, 0.72, 0.5, 1) forwards;
  }

  @keyframes badge-confetti-fall {
    0% { transform: translateY(0) rotate(0deg); opacity: 0; }
    12% { opacity: 0.95; }
    100% { transform: translateY(440px) rotate(520deg); opacity: 0; }
  }

  .badge-medal {
    position: relative;
    display: grid;
    place-items: center;
    width: 84px;
    height: 84px;
    margin-bottom: 22px;
    border-radius: 999px;
    background: radial-gradient(circle at 30% 25%, rgba(203, 226, 219, 0.9), rgba(118, 165, 167, 0.4));
    box-shadow: 0 10px 30px rgba(97, 131, 122, 0.26);
    animation: badge-medal-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  .badge-medal::after {
    content: "";
    position: absolute;
    inset: -8px;
    border-radius: 999px;
    border: 1px solid rgba(118, 165, 167, 0.4);
    animation: badge-medal-halo 2.4s ease-out infinite;
  }

  .badge-medal-emoji {
    font-size: 40px;
    line-height: 1;
  }

  @keyframes badge-medal-pop {
    0% { transform: scale(0.4); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }

  @keyframes badge-medal-halo {
    0% { transform: scale(1); opacity: 0.7; }
    70% { transform: scale(1.28); opacity: 0; }
    100% { transform: scale(1.28); opacity: 0; }
  }

  .badge-eyebrow {
    margin: 0 0 14px;
    color: #61837a;
    font-size: 11px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }

  .badge-name {
    margin: 0 0 18px;
    font-family: "Nunito", system-ui, sans-serif;
    font-size: clamp(28px, 7vw, 38px);
    font-weight: 900;
    line-height: 1.06;
    letter-spacing: -0.04em;
    color: #303744;
    text-wrap: balance;
  }

  .badge-streak {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 22px;
    padding: 10px 18px;
    border: 1px solid rgba(97, 131, 122, 0.16);
    border-radius: 999px;
    background: rgba(203, 226, 219, 0.22);
  }

  .streak-number {
    font-size: 20px;
    font-weight: 900;
    color: #36495b;
  }

  .streak-label {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }

  .badge-unlock-message {
    max-width: 300px;
    margin: 0 0 8px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.55;
    color: #36495b;
    text-wrap: balance;
  }

  .badge-unlock-extra {
    max-width: 300px;
    margin: 0;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.5;
    color: #6b7280;
    text-wrap: balance;
  }

  .badge-close {
    width: min(100%, 300px);
    min-height: 54px;
    margin-top: 28px;
    padding: 15px 24px;

    border: none;
    border-radius: 16px;

    background: linear-gradient(135deg, #76a5a7 0%, #61837a 100%);
    color: #ffffff;

    font-size: 15px;
    font-weight: 900;

    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;

    box-shadow: 0 12px 28px rgba(97, 131, 122, 0.28);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .badge-close:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 34px rgba(97, 131, 122, 0.36);
  }

  .badge-close:active {
    transform: translateY(0);
  }

  .badge-card[data-theme-mode="dark"] {
    border-color: rgba(255, 255, 255, 0.08);
    background: #1a1a1a;
    color: #f3f5f4;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.52);
  }

  .badge-card[data-theme-mode="dark"] .badge-eyebrow {
    color: #9cc9c4;
  }

  .badge-card[data-theme-mode="dark"] .badge-name,
  .badge-card[data-theme-mode="dark"] .streak-number,
  .badge-card[data-theme-mode="dark"] .badge-unlock-message {
    color: #f3f5f4;
  }

  .badge-card[data-theme-mode="dark"] .badge-medal {
    background: radial-gradient(circle at 30% 25%, rgba(156, 201, 196, 0.34), rgba(118, 165, 167, 0.14));
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  }

  .badge-card[data-theme-mode="dark"] .badge-medal::after {
    border-color: rgba(156, 201, 196, 0.3);
  }

  .badge-card[data-theme-mode="dark"] .badge-streak {
    border-color: rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.05);
  }

  .badge-card[data-theme-mode="dark"] .streak-label,
  .badge-card[data-theme-mode="dark"] .badge-unlock-extra {
    color: rgba(243, 245, 244, 0.62);
  }

  @media (max-width: 480px) {
    .badge-card {
      padding: 32px 24px 26px;
      border-radius: 22px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .celebration-overlay,
    .badge-card {
      transition: none !important;
    }

    .badge-confetti {
      display: none;
    }

    .badge-medal,
    .badge-medal::after {
      animation: none !important;
    }
  }
`}</style>
    </div>,
    portalEl
  );
}